/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { HpeViewMode } from '@gh/builder-shared-data';
import {
  composeEntityMergers,
  entityFieldMergeBy,
  entityFieldsMerger,
  entityReplaceOrMergeWith,
  setEntityField,
} from '@gh/common-shared';
import { ID } from '@gh/common-shared-data';
import {
  getHpeComponentId,
  getHpeCostCenterId,
  getHpeTopId,
  HpeComponent,
  HpeCostCenter,
  HpeDetails,
  HpeElementType,
  HpeItem,
  HpeTop,
  hpeViewModeToFormMode,
} from '../../../resources/hpe-details.model';
import {
  getHpeBestComponentDetailsTab,
  hpeCcTable,
  hpeComponentTable,
  HpeDetailsState,
  HpeDetailsStateEvent,
  HpeDetailsStateEventType,
  hpeItemTable,
  hpeTopRefTable,
  hpeTopTable,
  hpeUomRefTable,
  queryHpeIsSelectionEmpty,
  queryHpeTops,
  initHistory,
  saveViewToHistory,
  addToHistory,
  undo,
  redo,
} from '../hpe-details.state';
import { hpeDetailsCcItemReducer } from './hpe-details-cc-item.reducers';
import { hpeDetailsCcReducer, mergeCostCenter } from './hpe-details-cc.reducers';
import { hpeDetailsComponentReducer } from './hpe-details-component.reducers';
import { mergeCuttingList } from './hpe-details-cutting-list.reducers';
import { selectionReducer } from './hpe-details-selection.reducers';
import { hpeDetailsTopReducer } from './hpe-details-top.reducers';

const mergeHpeTop = entityReplaceOrMergeWith(entityFieldsMerger<HpeTop>(['count', 'value']));
const mergeHpeCostCenter = entityReplaceOrMergeWith(entityFieldsMerger<HpeCostCenter>(['cost', 'salesTax', 'total']));
const mergeHpeComponent = entityReplaceOrMergeWith(composeEntityMergers(
  entityFieldsMerger<HpeComponent>(['quantity', 'cost', 'extension']),
  entityFieldMergeBy<HpeComponent, 'cuttingList'>('cuttingList', mergeCuttingList)));
const mergeHpeItem = entityReplaceOrMergeWith(composeEntityMergers(
  entityFieldsMerger<HpeItem>(['quantity', 'cost', 'extension']),
  entityFieldMergeBy<HpeItem, 'cuttingList'>('cuttingList', mergeCuttingList)));

export function hpeDetailsReducer(state: HpeDetailsState, event: HpeDetailsStateEvent): HpeDetailsState {
  switch (event.type) {
    case HpeDetailsStateEventType.Load:
      return loadDetails(state, event.details);
    case HpeDetailsStateEventType.LoadWithCostCenter:
      if (event.costCenterId && event.costCenter) {
        return mergeCostCenter(loadDetails(state, event.details), <ID>event.costCenterId, event.costCenter);
      } else {
        return loadDetails(state, event.details);
      }
    case HpeDetailsStateEventType.Merge:
      return mergeDetails(state, event.details); 
    case HpeDetailsStateEventType.InitHistory:
      return initHistory(state);
    case HpeDetailsStateEventType.SaveViewToHistory:
      return saveViewToHistory(state);
    case HpeDetailsStateEventType.AddToHistory:
      return addToHistory(state);
    case HpeDetailsStateEventType.Undo:
      return undo(state);
    case HpeDetailsStateEventType.Redo:
      return redo(state);
    case HpeDetailsStateEventType.CostCenterSelect:
    case HpeDetailsStateEventType.ComponentSelect:
    case HpeDetailsStateEventType.CostCenterItemSelect:
    case HpeDetailsStateEventType.DetailsTabSelect:
    case HpeDetailsStateEventType.NavigateToInvalid:
    case HpeDetailsStateEventType.NavigateToInvalidTop:
    case HpeDetailsStateEventType.HighlightInvalid:
    case HpeDetailsStateEventType.ResetFocusedCell:
      return selectionReducer(state, event);
    default:
      return mainFlowReducer(state, event);
  }
}

function mainFlowReducer(state: HpeDetailsState, event: HpeDetailsStateEvent): HpeDetailsState {
  switch (event.type) {
    case HpeDetailsStateEventType.CostCenterAdd:
    case HpeDetailsStateEventType.CostCenterSetField:
    case HpeDetailsStateEventType.CostCenterRemove:
    case HpeDetailsStateEventType.CostCenterLoad:
      return hpeDetailsCcReducer(state, event);
    case HpeDetailsStateEventType.TopSetField:
    case HpeDetailsStateEventType.TopCommit:
    case HpeDetailsStateEventType.TopReset:
    case HpeDetailsStateEventType.TopRemove:
      return hpeDetailsTopReducer(state, event);
    case HpeDetailsStateEventType.ComponentAddSet:
    case HpeDetailsStateEventType.ComponentSetField:
    case HpeDetailsStateEventType.ComponentRemove:
      return hpeDetailsComponentReducer(state, event);
    case HpeDetailsStateEventType.CuttingListItemAdd:
    case HpeDetailsStateEventType.CuttingListItemSetField:
    case HpeDetailsStateEventType.CuttingListItemRemove:
    case HpeDetailsStateEventType.ResetFormulaNeedPropagation:
      if (event.sourceType === HpeElementType.Component) {
        return hpeDetailsComponentReducer(state, event);
      } else {
        return hpeDetailsCcItemReducer(state, event);
      }
    case HpeDetailsStateEventType.CostCenterItemAddSet:
    case HpeDetailsStateEventType.CostCenterItemSetField:
    case HpeDetailsStateEventType.CostCenterItemRemove:
      return hpeDetailsCcItemReducer(state, event);
    case HpeDetailsStateEventType.UomAddSet:
      return hpeDetailsUomRefReducer(state, event);
    case HpeDetailsStateEventType.TopAddSet:
      return hpeDetailsTopRefReducer(state, event);
    default:
      return state;
  }
}

function mergeDetails(state: HpeDetailsState, details: HpeDetails): HpeDetailsState {
  const { hpTops, hpCostCenters, hpComponents, hpItems } = details;
  return {
    ...state,
    masterInfo: setEntityField(state.masterInfo, 'cost', details.cost),
    hpTops: hpeTopTable.replaceAll(
      state.hpTops,
      queryHpeTops(state).map(getHpeTopId),
      hpTops,
      mergeHpeTop),
    hpCostCenters: hpeCcTable.mergeAll(state.hpCostCenters, hpCostCenters, mergeHpeCostCenter),
    hpComponents: hpeComponentTable.mergeAll(state.hpComponents, hpComponents, mergeHpeComponent),
    hpItems: hpeItemTable.mergeAll(state.hpItems, hpItems, mergeHpeItem),
    isRecalculationNeeded: false,
  };
}

function loadDetails(state: HpeDetailsState, details: HpeDetails): HpeDetailsState {

  const {
    priceBookId, housePlanId, code, name, cost, uoms, tops, hpTops, hpCostCenters, hpComponents, hpItems, viewMode,
    masterZoneHouseEstimatePlansFolderId, memo, total,
  } = details;

  const componentsCcId = getHpeCostCenterId(hpCostCenters[0]);
  const topRefItems = hpeTopRefTable.insertAll(hpeTopRefTable.create(), tops);
  const uomRefItems = hpeUomRefTable.insertAll(hpeUomRefTable.create(), uoms);

  return {
    ...state,
    priceBookId,
    housePlanId,
    hpComponentsCcId: componentsCcId,
    masterInfo: { code, name, cost, masterZoneHouseEstimatePlansFolderId, total },
    uoms: uomRefItems,
    tops: topRefItems,
    hpTops: hpeTopTable.insertAll(hpeTopTable.create(), hpTops),
    hpCostCenters: hpeCcTable.insertAll(hpeCcTable.create(), hpCostCenters),
    hpComponents: hpeComponentTable.insertAll(hpeComponentTable.create(), hpComponents),
    hpItems: hpeItemTable.insertAll(hpeItemTable.create(), hpItems),
    selection: queryHpeIsSelectionEmpty(state) ? {
      costCenterId: componentsCcId,
      componentId: hpComponents.length ? getHpeComponentId(hpComponents[0]) : void 0,
      detailsTabName: getHpeBestComponentDetailsTab(hpComponents[0]),
      highlightInvalidFormula: false,
    } : state.selection,
    formMode: hpeViewModeToFormMode(viewMode),
    isRecalculationNeeded: false,
    isProcessChangesAsap: false,
    memo,
    isDirty: details.viewMode === HpeViewMode.Create,
  };
}

function hpeDetailsUomRefReducer(state: HpeDetailsState, event: HpeDetailsStateEvent): HpeDetailsState {
  if (event.type === HpeDetailsStateEventType.UomAddSet) {
    return setEntityField(state, 'uoms', hpeUomRefTable.insertAll(state.uoms, event.uomSet));
  } else {
    return state;
  }
}

function hpeDetailsTopRefReducer(state: HpeDetailsState, event: HpeDetailsStateEvent): HpeDetailsState {
  if (event.type === HpeDetailsStateEventType.TopAddSet) {
    return setEntityField(state, 'tops', hpeTopRefTable.insertAll(state.tops, event.topSet));
  } else {
    return state;
  }
}
